import {
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import {
  DEFAULT_OVERLAY_BACKGROUND_OPACITY,
  DEFAULT_IMAGE_DURATION_SECONDS,
  DEFAULT_TRANSITION_DURATION_MS,
  MAX_IMAGE_DURATION_SECONDS,
  MAX_TRANSITION_DURATION_MS,
  MIN_TRANSITION_DURATION_MS,
  MIN_IMAGE_DURATION_SECONDS,
  OFFER_TEXT_LIMITS,
  PLAYLIST_STATUS,
  TV_DISPLAY_FIT_MODES,
  TV_DISPLAY_MODES,
  TV_DISPLAY_ORIENTATIONS,
  TV_DISPLAY_TRANSITIONS,
  TV_MEDIA_TYPES,
  TV_PLAYLIST_DEFAULTS,
  TV_PLAYLIST_OVERLAY_DEFAULTS,
  TV_PLAYLIST_PRESENTATION_DEFAULTS,
  TV_TEXT_ALIGNMENTS,
  TV_TEXT_ANIMATIONS,
  TV_TEXT_POSITIONS,
} from "./tvDisplayConstants";
import {
  getTvDisplayPlaylistItemsCollectionRef,
  getTvDisplayPlaylistsCollectionRef,
  initializeTvDisplayConfig,
} from "./tvDisplayService";

const BATCH_LIMIT = 400;

export async function createTvPlaylist({ name, description = "" }) {
  await initializeTvDisplayConfig();

  const cleanName = validatePlaylistName(name);
  const cleanDescription = normalizePlaylistDescription(description);
  const playlistRef = doc(getTvDisplayPlaylistsCollectionRef());

  await setDoc(playlistRef, {
    name: cleanName,
    description: cleanDescription,
    ...TV_PLAYLIST_DEFAULTS,
    hasUnpublishedChanges: false,
    lastPublicationId: null,
    lastPublishedVersion: 0,
    lastPublishedAt: null,
    lastPublishedTargetCount: 0,
    presentation: getDefaultPlaylistPresentation(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return playlistRef.id;
}

export function subscribeToTvPlaylists(callback, errorCallback) {
  const q = query(getTvDisplayPlaylistsCollectionRef(), orderBy("createdAt", "desc"));
  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((entry) => normalizePlaylist(entry));
      if (typeof callback === "function") callback(rows);
    },
    (error) => {
      if (typeof errorCallback === "function") errorCallback(error);
    }
  );
}

export async function getTvPlaylist(playlistId) {
  if (!playlistId) throw new Error("Playlist id is required");
  const snapshot = await getDoc(doc(getTvDisplayPlaylistsCollectionRef(), String(playlistId)));
  if (!snapshot.exists()) throw new Error("Playlist not found");
  return normalizePlaylist(snapshot);
}

export async function updateTvPlaylist(playlistId, data = {}) {
  if (!playlistId) throw new Error("Playlist id is required");

  const payload = { updatedAt: serverTimestamp() };
  const contentFields = ["name", "description", "enabled", "loop", "presentation"];
  if ("name" in data) payload.name = validatePlaylistName(data.name);
  if ("description" in data) payload.description = normalizePlaylistDescription(data.description);
  if ("enabled" in data) payload.enabled = Boolean(data.enabled);
  if ("loop" in data) payload.loop = Boolean(data.loop);
  if ("status" in data) payload.status = String(data.status || PLAYLIST_STATUS.DRAFT);
  if ("itemCount" in data) payload.itemCount = Math.max(0, Number(data.itemCount || 0));
  if ("presentation" in data) payload.presentation = normalizePlaylistPresentation(data.presentation);
  if (contentFields.some((field) => field in data) && !("status" in data)) {
    payload.status = PLAYLIST_STATUS.DRAFT;
    payload.hasUnpublishedChanges = true;
  }

  await updateDoc(doc(getTvDisplayPlaylistsCollectionRef(), String(playlistId)), payload);
}

export async function renameTvPlaylist(playlistId, name) {
  return updateTvPlaylist(playlistId, { name });
}

export async function setTvPlaylistEnabled(playlistId, enabled) {
  return updateTvPlaylist(playlistId, { enabled });
}

export async function updatePlaylistPresentation(playlistId, presentation = {}) {
  return updateTvPlaylist(playlistId, { presentation });
}

export async function deleteTvPlaylist(playlistId) {
  if (!playlistId) throw new Error("Playlist id is required");

  const itemsRef = getTvDisplayPlaylistItemsCollectionRef(playlistId);
  const itemsSnapshot = await getDocs(itemsRef);
  const docs = itemsSnapshot.docs;

  for (let index = 0; index < docs.length; index += BATCH_LIMIT) {
    const batch = writeBatch(itemsSnapshot.query.firestore);
    docs.slice(index, index + BATCH_LIMIT).forEach((entry) => batch.delete(entry.ref));
    await batch.commit();
  }

  await deleteDoc(doc(getTvDisplayPlaylistsCollectionRef(), String(playlistId)));
}

export function subscribeToTvPlaylistItems(playlistId, callback, errorCallback) {
  if (!playlistId) {
    if (typeof callback === "function") callback([]);
    return () => {};
  }

  const q = query(
    getTvDisplayPlaylistItemsCollectionRef(playlistId),
    orderBy("order", "asc")
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((entry) => normalizePlaylistItem(entry));
      if (typeof callback === "function") callback(rows);
    },
    (error) => {
      if (typeof errorCallback === "function") errorCallback(error);
    }
  );
}

export async function addTvPlaylistItem(playlistId, media) {
  return addTvPlaylistItems(playlistId, [media]);
}

export async function addTvPlaylistItems(playlistId, mediaItems = []) {
  if (!playlistId) throw new Error("Playlist id is required");
  const cleanMedia = (Array.isArray(mediaItems) ? mediaItems : []).filter(Boolean);
  if (!cleanMedia.length) return { addedCount: 0 };

  const itemsRef = getTvDisplayPlaylistItemsCollectionRef(playlistId);
  const snapshot = await getDocs(query(itemsRef, orderBy("order", "asc")));
  const existingCount = snapshot.docs.length;
  let nextOrder = existingCount;
  const nowBatches = [];

  for (let index = 0; index < cleanMedia.length; index += BATCH_LIMIT - 1) {
    nowBatches.push(cleanMedia.slice(index, index + (BATCH_LIMIT - 1)));
  }

  let committed = 0;
  try {
    for (const group of nowBatches) {
      const batch = writeBatch(snapshot.query.firestore);
      const groupStartOrder = nextOrder;
      for (let groupIndex = 0; groupIndex < group.length; groupIndex += 1) {
        const media = group[groupIndex];
        const itemRef = doc(itemsRef);
        const order = groupStartOrder + groupIndex;
        batch.set(itemRef, {
          mediaId: String(media.id || ""),
          order,
          enabled: true,
          imageDurationSeconds: media.type === TV_MEDIA_TYPES.IMAGE ? DEFAULT_IMAGE_DURATION_SECONDS : null,
          overlay: getDefaultPlaylistItemOverlay(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      nextOrder += group.length;
      batch.update(doc(getTvDisplayPlaylistsCollectionRef(), String(playlistId)), {
        itemCount: Math.max(0, committed + existingCount + group.length),
        status: PLAYLIST_STATUS.DRAFT,
        hasUnpublishedChanges: true,
        updatedAt: serverTimestamp(),
      });
      await batch.commit();
      committed += group.length;
    }
  } catch (error) {
    await synchronizePlaylistItemCount(playlistId).catch(() => {});
    throw error;
  }

  return { addedCount: committed };
}

export async function removeTvPlaylistItem(playlistId, itemId) {
  if (!playlistId || !itemId) throw new Error("Playlist and item ids are required");
  const items = await listPlaylistItems(playlistId);
  const remaining = items.filter((item) => item.id !== String(itemId));
  const removed = items.length - remaining.length;
  if (!removed) return { removed: false, itemCount: remaining.length };

  const batch = writeBatch(items[0]?.ref?.firestore || doc(getTvDisplayPlaylistsCollectionRef(), String(playlistId)).firestore);
  batch.delete(doc(getTvDisplayPlaylistItemsCollectionRef(playlistId), String(itemId)));
  remaining.forEach((item, index) => {
    batch.update(doc(getTvDisplayPlaylistItemsCollectionRef(playlistId), item.id), {
      order: index,
      updatedAt: serverTimestamp(),
    });
  });
  batch.update(doc(getTvDisplayPlaylistsCollectionRef(), String(playlistId)), {
    itemCount: Math.max(0, remaining.length),
    status: PLAYLIST_STATUS.DRAFT,
    hasUnpublishedChanges: true,
    updatedAt: serverTimestamp(),
  });
  await batch.commit();

  return { removed: true, itemCount: remaining.length };
}

export async function updateTvPlaylistItem(playlistId, itemId, data = {}) {
  if (!playlistId || !itemId) throw new Error("Playlist and item ids are required");

  const payload = { updatedAt: serverTimestamp() };
  if ("enabled" in data) payload.enabled = Boolean(data.enabled);
  if ("order" in data) payload.order = Math.max(0, Number(data.order || 0));
  if ("imageDurationSeconds" in data) {
    payload.imageDurationSeconds = normalizeImageDuration(data.imageDurationSeconds);
  }
  if ("overlay" in data) {
    payload.overlay = normalizePlaylistItemOverlay(data.overlay);
  }

  await updateDoc(doc(getTvDisplayPlaylistItemsCollectionRef(playlistId), String(itemId)), payload);
  await updateDoc(doc(getTvDisplayPlaylistsCollectionRef(), String(playlistId)), {
    status: PLAYLIST_STATUS.DRAFT,
    hasUnpublishedChanges: true,
    updatedAt: serverTimestamp(),
  });
}

export async function updatePlaylistItemOverlay(playlistId, itemId, overlay = {}) {
  return updateTvPlaylistItem(playlistId, itemId, { overlay });
}

export async function reorderTvPlaylistItems(playlistId, orderedItems = []) {
  if (!playlistId) throw new Error("Playlist id is required");
  const cleanItems = (Array.isArray(orderedItems) ? orderedItems : []).filter(Boolean);
  if (!cleanItems.length) return { itemCount: 0 };

  for (let index = 0; index < cleanItems.length; index += BATCH_LIMIT - 1) {
    const group = cleanItems.slice(index, index + (BATCH_LIMIT - 1));
    const batch = writeBatch(doc(getTvDisplayPlaylistsCollectionRef(), String(playlistId)).firestore);

    group.forEach((item) => {
      batch.update(doc(getTvDisplayPlaylistItemsCollectionRef(playlistId), String(item.id)), {
        order: Math.max(0, Number(item.order || 0)),
        updatedAt: serverTimestamp(),
      });
    });

    if (index === 0) {
      batch.update(doc(getTvDisplayPlaylistsCollectionRef(), String(playlistId)), {
        itemCount: cleanItems.length,
        status: PLAYLIST_STATUS.DRAFT,
        hasUnpublishedChanges: true,
        updatedAt: serverTimestamp(),
      });
    }

    await batch.commit();
  }

  return { itemCount: cleanItems.length };
}

export function buildNormalizedPlaylistOrder(items = []) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    ...item,
    order: index,
  }));
}

export function movePlaylistItem(items = [], fromIndex, toIndex) {
  const list = [...(Array.isArray(items) ? items : [])];
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= list.length ||
    toIndex >= list.length ||
    fromIndex === toIndex
  ) {
    return buildNormalizedPlaylistOrder(list);
  }

  const [moved] = list.splice(fromIndex, 1);
  list.splice(toIndex, 0, moved);
  return buildNormalizedPlaylistOrder(list);
}

export async function synchronizePlaylistItemCount(playlistId) {
  if (!playlistId) throw new Error("Playlist id is required");
  const snapshot = await getDocs(getTvDisplayPlaylistItemsCollectionRef(playlistId));
  await updateDoc(doc(getTvDisplayPlaylistsCollectionRef(), String(playlistId)), {
    itemCount: Math.max(0, snapshot.size),
    updatedAt: serverTimestamp(),
  });
  return snapshot.size;
}

async function listPlaylistItems(playlistId) {
  const snapshot = await getDocs(query(getTvDisplayPlaylistItemsCollectionRef(playlistId), orderBy("order", "asc")));
  return snapshot.docs.map((entry) => ({
    id: entry.id,
    ref: entry.ref,
    ...normalizePlaylistItem(entry),
  }));
}

function normalizePlaylist(snapshot) {
  const data = snapshot.data() || {};
  const presentation = normalizePlaylistPresentation(data.presentation);
  return {
    id: snapshot.id,
    name: String(data.name || ""),
    description: String(data.description || ""),
    enabled: data.enabled !== false,
    loop: data.loop !== false,
    status: String(data.status || PLAYLIST_STATUS.DRAFT),
    hasUnpublishedChanges: data.hasUnpublishedChanges === true,
    itemCount: Math.max(0, Number(data.itemCount || 0)),
    lastPublicationId: data.lastPublicationId || null,
    lastPublishedVersion: Math.max(0, Number(data.lastPublishedVersion || 0)),
    lastPublishedAt: data.lastPublishedAt || null,
    lastPublishedTargetCount: Math.max(0, Number(data.lastPublishedTargetCount || 0)),
    presentation,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    createdAtMs: toMillis(data.createdAt),
    updatedAtMs: toMillis(data.updatedAt),
    searchText: `${data.name || ""} ${data.description || ""}`.toLowerCase(),
  };
}

function normalizePlaylistItem(snapshot) {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    mediaId: String(data.mediaId || ""),
    order: Math.max(0, Number(data.order || 0)),
    enabled: data.enabled !== false,
    imageDurationSeconds: data.imageDurationSeconds == null ? null : Number(data.imageDurationSeconds || 0),
    overlay: normalizePlaylistItemOverlay(data.overlay),
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    createdAtMs: toMillis(data.createdAt),
    updatedAtMs: toMillis(data.updatedAt),
  };
}

function validatePlaylistName(value) {
  const clean = String(value || "").trim();
  if (!clean) throw new Error("Playlist name is required.");
  if (clean.length > 120) throw new Error("Playlist name is too long.");
  return clean;
}

function normalizePlaylistDescription(value) {
  const clean = String(value || "").trim();
  if (clean.length > 500) throw new Error("Playlist description is too long.");
  return clean;
}

function normalizeImageDuration(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) throw new Error("Image duration must be a number.");
  if (amount < MIN_IMAGE_DURATION_SECONDS) {
    throw new Error(`Image duration must be at least ${MIN_IMAGE_DURATION_SECONDS} seconds.`);
  }
  if (amount > MAX_IMAGE_DURATION_SECONDS) {
    throw new Error(`Image duration must be ${MAX_IMAGE_DURATION_SECONDS} seconds or less.`);
  }
  return Math.round(amount);
}

export function getDefaultPlaylistPresentation() {
  return {
    ...TV_PLAYLIST_PRESENTATION_DEFAULTS,
    textDefaults: { ...TV_PLAYLIST_PRESENTATION_DEFAULTS.textDefaults },
  };
}

export function getDefaultPlaylistItemOverlay(overrides = {}) {
  return normalizePlaylistItemOverlay({
    ...TV_PLAYLIST_OVERLAY_DEFAULTS,
    ...overrides,
  });
}

export function normalizePlaylistPresentation(value = {}) {
  const source = value && typeof value === "object" ? value : {};
  const base = getDefaultPlaylistPresentation();

  return {
    displayMode: pickEnum(source.displayMode, TV_DISPLAY_MODES, base.displayMode),
    orientation: pickEnum(source.orientation, TV_DISPLAY_ORIENTATIONS, base.orientation),
    fitMode: pickEnum(source.fitMode, TV_DISPLAY_FIT_MODES, base.fitMode),
    transition: pickEnum(source.transition, TV_DISPLAY_TRANSITIONS, base.transition),
    transitionDurationMs: normalizeTransitionDuration(source.transitionDurationMs ?? base.transitionDurationMs),
    backgroundColor: normalizeHexColor(source.backgroundColor, base.backgroundColor),
    defaultImageDurationSeconds: normalizeImageDuration(
      source.defaultImageDurationSeconds ?? base.defaultImageDurationSeconds
    ),
    showTextOverlay: source.showTextOverlay !== false,
    textDefaults: normalizeTextDefaults(source.textDefaults),
  };
}

export function validatePlaylistPresentation(value = {}) {
  return normalizePlaylistPresentation(value);
}

export function normalizePlaylistItemOverlay(value = {}) {
  const source = value && typeof value === "object" ? value : {};
  const base = getDefaultPlaylistItemOverlayBase();

  return {
    enabled: Boolean(source.enabled),
    badge: normalizeLimitedText(source.badge, OFFER_TEXT_LIMITS.badge),
    headline: normalizeLimitedText(source.headline, OFFER_TEXT_LIMITS.headline),
    subheadline: normalizeLimitedText(source.subheadline, OFFER_TEXT_LIMITS.subheadline),
    priceText: normalizeLimitedText(source.priceText, OFFER_TEXT_LIMITS.priceText),
    ctaText: normalizeLimitedText(source.ctaText, OFFER_TEXT_LIMITS.ctaText),
    position: pickEnum(source.position, TV_TEXT_POSITIONS, base.position),
    textAlign: pickEnum(source.textAlign, TV_TEXT_ALIGNMENTS, base.textAlign),
    textColor: normalizeHexColor(source.textColor, base.textColor),
    accentColor: normalizeHexColor(source.accentColor, base.accentColor),
    backgroundColor: normalizeHexColor(source.backgroundColor, base.backgroundColor),
    backgroundOpacity: normalizeOpacity(source.backgroundOpacity),
    animation: pickEnum(source.animation, TV_TEXT_ANIMATIONS, base.animation),
  };
}

export function validateOverlaySettings(value = {}) {
  return normalizePlaylistItemOverlay(value);
}

function normalizeTextDefaults(value = {}) {
  const source = value && typeof value === "object" ? value : {};
  const base = TV_PLAYLIST_PRESENTATION_DEFAULTS.textDefaults;
  return {
    textColor: normalizeHexColor(source.textColor, base.textColor),
    accentColor: normalizeHexColor(source.accentColor, base.accentColor),
    backgroundColor: normalizeHexColor(source.backgroundColor, base.backgroundColor),
    backgroundOpacity: normalizeOpacity(source.backgroundOpacity),
    position: pickEnum(source.position, TV_TEXT_POSITIONS, base.position),
    textAlign: pickEnum(source.textAlign, TV_TEXT_ALIGNMENTS, base.textAlign),
    animation: pickEnum(source.animation, TV_TEXT_ANIMATIONS, base.animation),
  };
}

function getDefaultPlaylistItemOverlayBase() {
  return {
    ...TV_PLAYLIST_OVERLAY_DEFAULTS,
  };
}

function normalizeTransitionDuration(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return DEFAULT_TRANSITION_DURATION_MS;
  return Math.max(MIN_TRANSITION_DURATION_MS, Math.min(MAX_TRANSITION_DURATION_MS, Math.round(amount)));
}

function normalizeOpacity(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return DEFAULT_OVERLAY_BACKGROUND_OPACITY;
  return Math.max(0, Math.min(1, Number(amount.toFixed(2))));
}

function normalizeHexColor(value, fallback) {
  const clean = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(clean)) return clean.toUpperCase();
  return fallback;
}

function pickEnum(value, enumMap, fallback) {
  const allowed = new Set(Object.values(enumMap));
  return allowed.has(value) ? value : fallback;
}

function normalizeLimitedText(value, maxLength) {
  const clean = String(value || "").trim();
  if (clean.length > maxLength) {
    throw new Error(`Text exceeds maximum length of ${maxLength} characters.`);
  }
  return clean;
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const stamp = new Date(value).getTime();
  return Number.isFinite(stamp) ? stamp : 0;
}
